"use client";

import {
  createContext,
  useContext,
  useMemo,
  useRef,
  PropsWithChildren,
} from "react";

type WorkflowTriggerContextValue = {
  trigger: () => void;
};

const WorkflowTriggerContext =
  createContext<WorkflowTriggerContextValue | null>(null);

export function useWorkflowTrigger() {
  const ctx = useContext(WorkflowTriggerContext);
  if (!ctx)
    throw new Error(
      "useWorkflowTrigger must be used within <WorkflowTriggerProvider>",
    );
  return ctx;
}

type WorkflowTriggerProviderProps = PropsWithChildren<{
  onTrigger?: () => void;
}>;

export function WorkflowTriggerProvider({
  onTrigger,
  children,
}: WorkflowTriggerProviderProps) {
  const canonicalBtnRef = useRef<HTMLButtonElement | null>(null);

  const trigger = useMemo(
    () => () => {
      onTrigger?.();
      canonicalBtnRef.current?.click();
    },
    [onTrigger],
  );

  return (
    <WorkflowTriggerContext.Provider value={{ trigger }}>
      <button
        ref={canonicalBtnRef}
        data-trigger-bot-from-api={true}
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        style={{
          position: "absolute",
          width: 0,
          height: 0,
          overflow: "hidden",
          pointerEvents: "none",
        }}
      />
      {children}
    </WorkflowTriggerContext.Provider>
  );
}
